import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useAuth } from "@/app";
import { MiniAppHomePage } from "@/pages/miniapp";
import { GenerationRoute } from "@/shared/components/GenerationRoute/GenerationRoute";
import { ProtectedRoute } from "@/shared/components/ProtectedRoute";
import { useShareDeepLink, isShareDeepLinkPending, getShareRedirectLocation, } from "@/shared/lib/shareDeepLink";
import { isLandingActive } from "@/shared/lib/landingActive";
import { isTelegramMiniApp } from "@/shared/lib/telegram";
import { isMaxMiniApp } from "@/shared/lib/max";
import { cn } from "@/shared/lib/utils";
import { Loader } from "@/shared/ui";
import React, { Suspense, lazy } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import styles from "./MiniAppContent.module.scss";
// Code-splitting: страницы, на которые юзер обычно не попадает в первой
// сессии, грузятся отдельными чанками по требованию. `MiniAppHomePage` и
// `GenerateSongPage` остаются eager — это горячий путь / редирект-таргет,
// лишний Suspense flicker тут не нужен.
//
// Импортируем напрямую из файла страницы (минуя `@/pages` barrel), чтобы
// webpack создал чистую async-границу для каждой страницы и не тянул в
// чанк весь barrel.
const PaymentSuccessPage = lazy(() => import("@/pages/web/PaymentSuccessPage").then((m) => ({
    default: m.PaymentSuccessPage,
})));
const BalancePage = lazy(() => import("@/pages/web/BalancePage").then((m) => ({ default: m.BalancePage })));
const ReferralPage = lazy(() => import("@/pages/web/ReferralPage").then((m) => ({ default: m.ReferralPage })));
const ChartsPage = lazy(() => import("@/pages/web/ChartsPage").then((m) => ({ default: m.ChartsPage })));
const FAQPage = lazy(() => import("@/pages/web/FAQPage").then((m) => ({ default: m.FAQPage })));
const MySongsPage = lazy(() => import("@/pages/web/MySongsPage").then((m) => ({ default: m.MySongsPage })));
const ExtendSongPage = lazy(() => import("@/pages/web/ExtendSongPage").then((m) => ({
    default: m.ExtendSongPage,
})));
const VideoCoverPage = lazy(() => import("@/pages/web/VideoCoverPage").then((m) => ({
    default: m.VideoCoverPage,
})));
const KaraokePage = lazy(() => import("@/pages/web/KaraokePage").then((m) => ({
    default: m.KaraokePage,
})));
const ReleasePage = lazy(() => import("@/pages/web/ReleasePage").then((m) => ({
    default: m.ReleasePage,
})));
const MyReleasesPage = lazy(() => import("@/pages/web/MyReleasesPage").then((m) => ({
    default: m.MyReleasesPage,
})));
const UiKitPage = lazy(() => import("@/pages/dev/UiKitPage").then((m) => ({ default: m.UiKitPage })));
const ShareCreatePage = lazy(() => import("@/pages/web/ShareCreatePage").then((m) => ({
    default: m.ShareCreatePage,
})));
// Лендинг (WelcomePage) в мобильном браузере на "/" — тот же web-лендинг, что и
// на десктопе (responsive, свой navbar + sticky CTA). В реальном TG/MAX-шелле не
// показываем (там авторизованный UX).
const WelcomePage = lazy(() => import("@/pages/web/WelcomePage").then((m) => ({ default: m.WelcomePage })));
// Пейвол-воронка. Публичного роута НЕТ — показывается только через AB-гейт
// `/balance` (BalanceGate) юзерам с paywall_variant === "new".
const PaywallPage = lazy(() => import("@/pages/web/PaywallPage").then((m) => ({ default: m.PaywallPage })));
const RouteFallback = () => (_jsx("div", { className: styles.routeFallback, children: _jsx(Loader, { size: "lg" }) }));
/**
 * AB-гейт пополнения `/balance` в миниаппе. `paywall_variant === "new"` (сплит
 * на бэке, приходит в user) → страница-пейвол вместо баланса; `current`/дефолт →
 * прежний BalancePage. Пока user грузится (isLoading) — лоадер, без мигания
 * BalancePage перед пейволом.
 */
const BalanceGate = ({ language }) => {
    const { user, isLoading } = useAuth();
    if (isLoading) {
        return _jsx(RouteFallback, {});
    }
    if (user?.paywall_variant === "new") {
        return _jsx(PaywallPage, { language: language });
    }
    return _jsx(BalancePage, { language: language });
};
export const MiniAppContent = ({ language, isLoggedIn, openRegisterModal, openLoginModal, darkMode, }) => {
    const location = useLocation();
    const { user, isFreshGuestSession } = useAuth();
    // Лендинг активен на "/" (welcome-вариант свежего гостя ИЛИ превью `?welcome=1`)
    // — тот же предикат, что в web-HomeGate и App.tsx (isMiniAppWelcome). В реальном
    // TG/MAX-шелле web-лендинг не показываем. Раньше мобильный путь вообще не
    // рендерил WelcomePage: "/" уводил гостя на дашборд, анонима — на /generate-song.
    const isRealMiniAppShell = isTelegramMiniApp() || isMaxMiniApp();
    const landingActive = !isRealMiniAppShell &&
        isLandingActive({
            pathname: location.pathname,
            search: location.search,
            isFreshGuestSession,
            welcomeVariant: user?.welcome_page_variant,
        });
    // MAX открывает миниап по КОРНЕВОМУ URL (без диплинков на путь), а payload
    // «поделиться песней» прилетает в start-параметре. Дерево миниаппа рендерится
    // здесь (минуя web-`Content`), поэтому рут-обработчик deep-link держим и тут —
    // иначе на MAX кнопка «Поделиться» открывала бы главную вместо `/share`.
    useShareDeepLink();
    // Используем редизайн-версии страниц везде — и в мобильном браузере,
    // и в Telegram WebApp. Старые MiniApp-страницы (MiniAppGenerateSongPage,
    // MiniAppChartsPage и т.д.) больше не подключаются — у пользователя
    // одинаковый UX независимо от точки входа. Исключение —
    // MiniAppHomePage: это и есть актуальная редизайн-главная (дашборд)
    // для авторизованного пользователя.
    // Гости с `/` сразу на /generate-song (и classic, и chat).
    // search сохраняем — иначе VK/рекламные ?click_id=&utm_* пропадают из URL.
    // hash сохраняем — в нём MAX/TG держат данные запуска (start_param + initData);
    // без него редирект гостя затирал бы payload «поделиться» (share-audio_<id>)
    // и подписанную initData → deep-link на /share терялся, авторизация падала.
    //
    // Стартовый share-диплинк (кнопка «Поделиться» из бота): на `/` ДЕКЛАРАТИВНО
    // редиректим на `/share?audio_id=<id>` — детерминированно, синхронно с
    // рендером, приоритетнее гостевого редиректа/дашборда. Через эффект это
    // терялось (гонка с переключением шеллов Content↔MiniAppContent).
    const shareRedirect = isShareDeepLinkPending()
        ? getShareRedirectLocation()
        : null;
    const HomeRoute = shareRedirect ? (_jsx(Navigate, { to: shareRedirect, replace: true })) : landingActive ? (_jsx(WelcomePage, { openLoginModal: openLoginModal })) : !isLoggedIn ? (_jsx(Navigate, { to: {
            pathname: "/generate-song",
            search: location.search,
            hash: location.hash,
        }, replace: true })) : (_jsx(MiniAppHomePage, { openLoginModal: openLoginModal, openRegisterModal: openRegisterModal }));
    const GenerateSongRoute = (_jsx(GenerationRoute, { language: language, darkMode: darkMode, openLoginModal: openLoginModal, openRegisterModal: openRegisterModal }));
    const ChartsRoute = (_jsx(ChartsPage, { language: language, darkMode: darkMode, openLoginModal: openLoginModal }));
    const BalanceRoute = _jsx(BalanceGate, { language: language });
    const FAQRoute = _jsx(FAQPage, { language: language });
    const MySongsRoute = _jsx(MySongsPage, { language: language, darkMode: darkMode });
    const ExtendSongRoute = (_jsx(ProtectedRoute, { requireAuth: true, children: _jsx(ExtendSongPage, { language: language, darkMode: darkMode }) }));
    const VideoCoverRoute = (_jsx(ProtectedRoute, { requireAuth: true, children: _jsx(VideoCoverPage, { language: language, darkMode: darkMode }) }));
    const KaraokeRoute = (_jsx(ProtectedRoute, { requireAuth: true, children: _jsx(KaraokePage, { language: language, darkMode: darkMode }) }));
    const ReleaseRoute = (_jsx(ProtectedRoute, { requireAuth: true, children: _jsx(ReleasePage, { language: language, darkMode: darkMode, openLoginModal: openLoginModal }) }));
    // Список заявок — под той же защитой, что и сама заявка: гостю релиз-ручки
    // бэка отвечают 403. Гостя `ProtectedRoute` пропускает (гостевая сессия для
    // него — «залогинен»), поэтому странице нужен `openLoginModal`.
    const MyReleasesRoute = (_jsx(ProtectedRoute, { requireAuth: true, children: _jsx(MyReleasesPage, { language: language, darkMode: darkMode, openLoginModal: openLoginModal }) }));
    if (!isLoggedIn) {
        return (_jsx("div", { className: cn(styles.content, landingActive && styles.contentLanding), "data-scroll-root": "miniapp", children: _jsxs("div", { className: styles.contentWrapper, children: [_jsx(Suspense, { fallback: _jsx(RouteFallback, {}), children: _jsxs(Routes, { children: [_jsx(Route, { path: "/", element: HomeRoute }), _jsx(Route, { path: "/generate-song", element: GenerateSongRoute }), _jsx(Route, { path: "/payment/success", element: _jsx(ProtectedRoute, { requireAuth: true, children: _jsx(PaymentSuccessPage, { language: language }) }) }), _jsx(Route, { path: "/faq", element: FAQRoute }), _jsx(Route, { path: "/charts", element: ChartsRoute }), _jsx(Route, { path: "/share", element: _jsx(ShareCreatePage, {}) }), _jsx(Route, { path: "/ui", element: _jsx(UiKitPage, {}) })] }) }), !landingActive && _jsx("div", { className: styles.spacer })] }) }));
    }
    return (_jsx("div", { className: cn(styles.content, landingActive && styles.contentLanding), "data-scroll-root": "miniapp", children: _jsxs("div", { className: styles.contentWrapper, children: [_jsx(Suspense, { fallback: _jsx(RouteFallback, {}), children: _jsxs(Routes, { children: [_jsx(Route, { path: "/", element: HomeRoute }), _jsx(Route, { path: "/my-songs", element: MySongsRoute }), _jsx(Route, { path: "/extend-song", element: ExtendSongRoute }), _jsx(Route, { path: "/video-cover", element: VideoCoverRoute }), _jsx(Route, { path: "/karaoke", element: KaraokeRoute }), _jsx(Route, { path: "/release", element: ReleaseRoute }), _jsx(Route, { path: "/my-releases", element: MyReleasesRoute }), _jsx(Route, { path: "/generate-song", element: GenerateSongRoute }), _jsx(Route, { path: "/balance", element: _jsx(ProtectedRoute, { requireAuth: true, children: BalanceRoute }) }), _jsx(Route, { path: "/referral", element: _jsx(ProtectedRoute, { requireAuth: true, children: _jsx(ReferralPage, { language: language }) }) }), _jsx(Route, { path: "/payment/success", element: _jsx(ProtectedRoute, { requireAuth: true, children: _jsx(PaymentSuccessPage, { language: language }) }) }), _jsx(Route, { path: "/faq", element: FAQRoute }), _jsx(Route, { path: "/charts", element: ChartsRoute }), _jsx(Route, { path: "/share", element: _jsx(ShareCreatePage, {}) }), _jsx(Route, { path: "/ui", element: _jsx(UiKitPage, {}) })] }) }), !landingActive && _jsx("div", { className: styles.spacer })] }) }));
};
